/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 23/08/22
 */

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import WorkplanProvider from '@/components/contexts/WorkplanContext/WorkplanProvider';
import { Workplan, WorkplanYear } from '@/wpt-lib/interfaces/Workplan';
import { useAsyncLayoutEffect } from '@/utils/hooks/useAsyncEffect';
import { useStatefulRequestWithoutData } from '@/utils/useStatefulRequests/useStatefulRequestWithoutData';
import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

const LoadWorkplanProvider = ({
    children,
    getPath,
    method,
}: {
    children?: ReactNode;
    getPath: () => string;
    method?: 'GET' | 'POST';
}) => {
    const { currentAssignment } = useAssignment();
    const {
        makeRequest,
        error,
        loading,
        responseData: workplan,
    } = useStatefulRequestWithoutData<Workplan>(method ?? 'GET');

    const { makeRequest: yearsRequest } = useStatefulRequestWithoutData<WorkplanYear[]>('GET');

    const navigate = useNavigate();

    useAsyncLayoutEffect(async () => {
        if (!currentAssignment) return;

        try {
            if (currentAssignment.inactive && method === 'POST') {
                /* If assignment is inactive, it is not possible to create a new workplan, navigate to the
                 * last workplan of the assignment. */
                const { data } = await yearsRequest('workplan/years', {
                    params: {
                        assignment: currentAssignment.id,
                    },
                });

                if (!data || data.length === 0) return;
                data.sort((a, b) => b.year - a.year);

                const year = data[0];
                navigate(`/workplan/${year.id}/${Math.max(...year.versions)}`);
            } else {
                await makeRequest(getPath(), {
                    params: {
                        assignment: currentAssignment.id,
                    },
                });
            }
        } catch (e) {
            //handled in hook
        }
    }, [currentAssignment, getPath]);

    return (
        <WorkplanProvider loading={loading} error={error} workplan={workplan}>
            {children}
        </WorkplanProvider>
    );
};

export default LoadWorkplanProvider;
